import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { KeyValueType } from '@models/common';
import { FieldModel, ScreenFieldModel } from '@models/module/fields/main';
import { GridViewModel } from '@models/module/grid';
import { HttpService } from '@shared/services';

@Injectable({ providedIn: 'root' })
export class ScreenFieldsService {
  private readonly http = inject(HttpService);

  private moduleHeaders(moduleId: number) {
    return { moduleId: moduleId.toString() };
  }

  search(moduleId: number, screenId: number): Observable<ScreenFieldModel[]> {
    const params = this.http.buildHttpParams({ screenId });
    return this.http
      .post<
        GridViewModel<ScreenFieldModel[]>
      >('_api/ScreenFields/Search', {}, { useLoader: false, httpOptions: { params, headers: this.moduleHeaders(moduleId) } })
      .pipe(map(result => result?.data ?? []));
  }

  syncToScreen(moduleId: number, screenId: number, fields: ScreenFieldModel[], screenType?: number) {
    const params = this.http.buildHttpParams({ screenId, screenType });
    return this.http.put('_api/ScreenFields/SyncFieldToScreen', fields, {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  getFieldDetails(moduleId: number, id: number, screenId: number): Observable<FieldModel> {
    const params = this.http.buildHttpParams({ id, screenId });
    return this.http.get<FieldModel>('_api/ScreenFields/GetFieldDetails', {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  updateFieldDetails(moduleId: number, screenId: number, body: Partial<FieldModel>) {
    const params = this.http.buildHttpParams({ screenId });
    return this.http.put('_api/ScreenFields/UpdateScreenFieldDetails', body, {
      useLoader: false,
      httpOptions: { params, headers: this.moduleHeaders(moduleId) },
    });
  }

  getFieldScreens(moduleId: number, fieldId: number): Observable<KeyValueType<number, string>[]> {
    const params = this.http.buildHttpParams({ id: fieldId });
    return this.http
      .get<KeyValueType<number, string>[]>('_api/ScreenFields/ScreenFieldDict', {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      })
      .pipe(map(screens => screens ?? []));
  }

  getParentDefaultValues(moduleId: number, parentListId: number, screenId: number): Observable<number[]> {
    const params = this.http.buildHttpParams({ id: parentListId, screenId });
    return this.http
      .get<number[]>('_api/ScreenFields/CheckParentDataSourceHasDefaultValue', {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      })
      .pipe(map(values => values ?? []));
  }

  hasContractFields(moduleId: number, screenId: number): Observable<boolean> {
    const params = this.http.buildHttpParams({ screenId });
    return this.http
      .get<boolean>('_api/ScreenFields/ScreenHasContractFields', {
        useLoader: false,
        httpOptions: { params, headers: this.moduleHeaders(moduleId) },
      })
      .pipe(map(result => !!result));
  }
}
